import React, { useState, useEffect } from 'react';
import { lawApi } from '../../lawApi';
import { useTheme } from '../../components/LawPortalLayout';
import { FileText, ChevronDown, ChevronUp, CheckCircle2, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = ['not_started', 'in_progress', 'waiting_on_student', 'under_advisor_review', 'ready_to_submit', 'submitted', 'complete'];
const STATUS_STYLES = {
  not_started: { label: 'Not Started', dot: 'bg-gray-400' },
  in_progress: { label: 'In Progress', dot: 'bg-blue-500' },
  waiting_on_student: { label: 'Waiting on Student', dot: 'bg-amber-500' },
  under_advisor_review: { label: 'Under Review', dot: 'bg-purple-500' },
  ready_to_submit: { label: 'Ready to Submit', dot: 'bg-emerald-400' },
  submitted: { label: 'Submitted', dot: 'bg-emerald-600' },
  complete: { label: 'Complete', dot: 'bg-emerald-700' },
};

export default function LawApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filter, setFilter] = useState('all');
  const theme = useTheme();
  const { dark, textPrimary, textSecondary, textMuted, borderColor, hoverBg } = theme;
  const cardBg = dark ? 'bg-[#141618]' : 'bg-white';
  const inputClass = `w-full h-8 px-2.5 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white text-[#1A1A1A]'} focus:outline-none`;

  const load = () => lawApi.getApplications().then(setApps).catch(() => toast.error('Failed to load')).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const handleUpdate = async (id, field, value) => {
    try { await lawApi.updateApplication(id, { [field]: value }); load(); } catch (e) { toast.error(e.message); }
  };

  const filtered = filter === 'all' ? apps : filter === 'active' ? apps.filter(a => !['submitted', 'complete'].includes(a.status)) : apps.filter(a => ['submitted', 'complete'].includes(a.status));

  if (loading) return <div className={`h-64 animate-pulse rounded-lg ${dark ? 'bg-[#1C1E20]' : 'bg-[#E8E6E1]'}`} />;

  return (
    <div className="space-y-5" data-testid="law-applications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-lg font-semibold ${textPrimary}`} style={{ fontFamily: "'Inter', sans-serif" }}>Applications</h1>
          <p className={`text-[11px] ${textMuted} mt-0.5`}>{apps.length} application{apps.length !== 1 ? 's' : ''} · {apps.filter(a => a.status === 'submitted').length} submitted</p>
        </div>
        <div className="flex gap-1">
          {['all', 'active', 'completed'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1 text-[10px] rounded capitalize ${filter === f ? 'bg-[#1A1A1A] text-white' : `${dark ? 'text-[#888]' : 'text-[#666]'} ${hoverBg}`}`}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 && (
        <div className={`${cardBg} border ${borderColor} rounded-lg p-8 text-center`}>
          <FileText className={`h-8 w-8 ${textMuted} mx-auto mb-2`} strokeWidth={1} />
          <p className={`text-sm ${textSecondary}`}>{apps.length === 0 ? 'No applications yet' : 'No matching applications'}</p>
          <p className={`text-xs ${textMuted} mt-1`}>{apps.length === 0 ? 'Add schools to your list to generate applications' : 'Try a different filter'}</p>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(app => {
          const st = STATUS_STYLES[app.status] || STATUS_STYLES.not_started;
          const isExpanded = expanded === app.id;
          return (
            <div key={app.id} className={`${cardBg} border ${borderColor} rounded-lg overflow-hidden`} data-testid={`law-app-${app.id}`}>
              <button onClick={() => setExpanded(isExpanded ? null : app.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${hoverBg} transition-colors`}>
                <div className={`w-2 h-2 rounded-full ${st.dot}`} />
                <span className={`text-sm font-medium flex-1 ${textPrimary}`}>{app.school_name}</span>
                <span className={`text-[10px] ${textMuted} uppercase`}>{app.application_type}</span>
                {app.deadline && <span className={`text-[10px] font-mono ${textSecondary}`}>{app.deadline}</span>}
                <span className={`px-2 py-0.5 text-[9px] rounded ${dark ? 'bg-[#1C1E20]' : 'bg-[#F2F1EE]'} ${textSecondary}`}>{st.label}</span>
                {isExpanded ? <ChevronUp className={`h-4 w-4 ${textMuted}`} /> : <ChevronDown className={`h-4 w-4 ${textMuted}`} />}
              </button>
              {isExpanded && (
                <div className={`px-4 pb-4 border-t ${borderColor} pt-3 space-y-3`}>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div><label className={`text-[10px] ${textMuted}`}>Status</label>
                      <select className={inputClass} value={app.status} onChange={e => handleUpdate(app.id, 'status', e.target.value)}>
                        {STATUSES.map(s => <option key={s} value={s}>{STATUS_STYLES[s]?.label || s}</option>)}
                      </select></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Type</label>
                      <select className={inputClass} value={app.application_type || 'RD'} onChange={e => handleUpdate(app.id, 'application_type', e.target.value)}>
                        <option value="ED">Early Decision</option><option value="EA">Early Action</option><option value="RD">Regular Decision</option><option value="rolling">Rolling</option>
                      </select></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Decision</label>
                      <select className={inputClass} value={app.decision || ''} onChange={e => handleUpdate(app.id, 'decision', e.target.value || null)}>
                        <option value="">Pending</option><option value="admitted">Admitted</option><option value="waitlisted">Waitlisted</option><option value="denied">Denied</option>
                      </select></div>
                    <div><label className={`text-[10px] ${textMuted}`}>Submitted</label>
                      <input className={inputClass} type="date" value={app.submitted_date || ''} onChange={e => handleUpdate(app.id, 'submitted_date', e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <label className={`flex items-center gap-2 text-xs ${textSecondary}`}>
                      <input type="checkbox" checked={app.resume_uploaded || false} onChange={e => handleUpdate(app.id, 'resume_uploaded', e.target.checked)} className="rounded" />
                      Resume uploaded
                    </label>
                    <label className={`flex items-center gap-2 text-xs ${textSecondary}`}>
                      <input type="checkbox" checked={app.fee_paid || false} onChange={e => handleUpdate(app.id, 'fee_paid', e.target.checked)} className="rounded" />
                      Fee paid
                    </label>
                    <label className={`flex items-center gap-2 text-xs ${textSecondary}`}>
                      <input type="checkbox" checked={app.addenda_required || false} onChange={e => handleUpdate(app.id, 'addenda_required', e.target.checked)} className="rounded" />
                      Addenda required
                    </label>
                    <label className={`flex items-center gap-2 text-xs ${textSecondary}`}>
                      <input type="checkbox" checked={app.deposit_paid || false} onChange={e => handleUpdate(app.id, 'deposit_paid', e.target.checked)} className="rounded" />
                      Deposit paid
                    </label>
                  </div>
                  {app.scholarship_offered && (
                    <div className={`text-xs ${textSecondary}`}>Scholarship: <strong>{app.scholarship_offered}</strong></div>
                  )}
                  <div><label className={`text-[10px] ${textMuted}`}>Notes</label>
                    <textarea className={`w-full px-2.5 py-2 text-xs rounded border ${borderColor} ${dark ? 'bg-[#1C1E20] text-[#F0EFEC]' : 'bg-white'} focus:outline-none`}
                      value={app.notes || ''} onChange={e => handleUpdate(app.id, 'notes', e.target.value)} rows={2} placeholder="Application notes..." /></div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
